import { useEffect, useRef, useState } from 'react'
import Layout from 'containers/Layout'
import { useHistory, useParams } from 'react-router-dom'
import apiGet from 'lib/network/apiGet'
import apiPost from 'lib/network/apiPost'
import notify from 'components/atoms/Notify'
import { forEach, includes, isObject } from 'lodash'
import { useTranslation } from 'react-i18next'
import EditBookingRedesign from './EditBookingRedesign'

const AddEditBooking = ({ readOnly }) => {
  const history = useHistory()
  const [bookingData, setBookingData] = useState({})
  const [loading, setLoading] = useState(true)
  const [accessError, setAccessError] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const hasLoadedOnceRef = useRef(false)
  const [isCopyingUrl, setIsCopyingUrl] = useState(false)
  const id = useParams()?.id
  const { t } = useTranslation()
  const saveRef = useRef(null)

  useEffect(() => {
    if (id) {
      fetchBooking()
    }
    // eslint-disable-next-line
  }, [id])

  const fetchBooking = async ({ silent = false } = {}) => {
    if (silent) {
      setIsRefreshing(true)
    } else if (!hasLoadedOnceRef.current) {
      setLoading(true)
    }

    try {
      const response = await apiGet(`booking/${id}`, null, false)

      if (!response?.success || !isObject(response.data)) {
        const isForbidden = response?.code === 403 || /access|permission/i.test(String(response?.message || ''))
        const message = isForbidden
          ? t('booking.messages.noPermission', { defaultValue: "You don't have permission to view this booking." })
          : response?.message || t('booking.messages.loadFailed', { defaultValue: 'Failed to load booking' })
        setAccessError(message)
        setBookingData({})
        if (!silent) notify.error(message)
        return
      }

      forEach(Object.entries(response.data), ([key, value]) => {
        if (value === 'null') {
          delete response.data[key]
        }
      })

      setAccessError(null)
      setBookingData(response.data)
      hasLoadedOnceRef.current = true
    } catch (error) {
      const message = error?.message || t('booking.messages.loadFailed', { defaultValue: 'Failed to load booking' })
      setAccessError(message)
      setBookingData({})
      if (!silent) notify.error(message)
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }

  const getBookingUrl = () => {
    const bookingId = id || bookingData?._id
    if (!bookingId) return null
    const base = process.env.REACT_APP_FRONTEND_HOST_URL || ''
    return `${base}/booking/direct/${bookingId}`
  }

  const handleCopyBookingUrl = async () => {
    const bookingId = id || bookingData?._id
    if (!bookingId) {
      notify.error('Booking URL not available')
      return
    }
    setIsCopyingUrl(true)
    try {
      const response = await apiPost(`booking/${bookingId}/token`, { createBookingToken: true })
      const token = response?.data?.token || response?.token
      let bookingUrl = getBookingUrl()
      if (token && bookingUrl) {
        const separator = includes(bookingUrl, '?') ? '&' : '?'
        bookingUrl = `${bookingUrl}${separator}token=${token}`
      }
      if (bookingUrl) {
        await navigator.clipboard.writeText(bookingUrl)
        notify.success('Booking URL copied to clipboard')
      }
    } catch (error) {
      notify.error('Failed to copy booking URL')
    } finally {
      setIsCopyingUrl(false)
    }
  }

  const ghostBtn =
    'inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-[13px] font-bold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none'
  const darkBtn = 'inline-flex items-center justify-center rounded-xl bg-[#2d2b55] px-4 py-2 text-[13px] font-bold text-white shadow-md hover:bg-[#25243d] focus:outline-none'

  return (
    <Layout
      headerRight={
        <div className="flex flex-wrap items-center gap-2">
          {!accessError && (
            <button type="button" onClick={handleCopyBookingUrl} disabled={isCopyingUrl} className={ghostBtn}>
              {isCopyingUrl ? 'Copying…' : 'Copy Booking URL'}
            </button>
          )}
          <button type="button" onClick={() => history.push('/bookings')} className={ghostBtn}>
            {t('addEditPartner.buttons.goBack') || 'Go Back'}
          </button>
          {/* {!accessError && !readOnly && (
            <button type="button" onClick={() => saveRef.current && saveRef.current()} className={darkBtn}>
              Save Changes
            </button>
          )} */}
        </div>
      }
      title={t('bookings.buttons.editBooking')}
      breadcrumbs={[
        { name: 'Booking List', pathname: '/bookings' },
        { name: `Edit Booking${bookingData?.bookingReferenceId ? ` #${bookingData.bookingReferenceId}` : ''}`, pathname: `/booking/edit/${id}/booking-profile`, current: true },
      ]}
    >
      <div className="relative w-full overflow-auto">
        {loading ? (
          <div className="flex h-[500px] w-full items-center justify-center text-slate-500">Loading…</div>
        ) : accessError ? (
          <div className="flex h-[500px] w-full flex-col items-center justify-center gap-4 px-4 text-center" role="alert">
            <p className="max-w-md text-base font-semibold text-slate-800">{accessError}</p>
            <button type="button" onClick={() => history.push('/bookings')} className={ghostBtn}>
              {t('addEditPartner.buttons.goBack') || 'Go Back'}
            </button>
          </div>
        ) : (
          <>
            {isRefreshing ? (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center bg-white/40 pt-6 text-sm text-slate-500">Refreshing…</div>
            ) : null}
            {/* Key on the rental car so an in-place car swap (same booking id) remounts the editor and
                re-initializes its form from the refetched booking — otherwise form.dailyRate etc. keep
                the old car's values. Normal saves keep the same car and do not remount. */}
            <EditBookingRedesign
              key={bookingData?.rentalCar?._id || id}
              bookingData={bookingData}
              id={id}
              refetch={() => fetchBooking({ silent: true })}
              saveRef={saveRef}
              readOnly={readOnly}
            />
          </>
        )}
      </div>
    </Layout>
  )
}

export default AddEditBooking
